import type { ColumnDefinition } from "../columnFilters/columnDefinition";

import {
  computeResizedWidth,
  MIN_COLUMN_WIDTH_PX,
  reclaimRemovedWidths,
  snapshotMissingWidths,
  TIME_COLUMN_ID,
  type ColumnWidths,
} from "./columnLayout";

/**
 * The one owner of column widths for a Log Panel.
 *
 * Widths used to live in four places at once — React state, a ref mirroring it
 * for synchronous reads, a rollback copy, and the persisted panel config — with
 * every resize handler responsible for keeping them agreeing. Each new
 * interaction path (a click that never moved, a second concurrent pointer, a
 * column disappearing mid-drag) was another chance for two of them to diverge.
 *
 * Folding them into one state value with explicit transitions removes that
 * class of bug rather than patching its instances:
 *
 *  - The rollback baseline lives *inside* the drag, so it cannot outlive it or
 *    be overwritten by a second drag.
 *  - A single `drag` slot makes one-resize-at-a-time structural.
 *  - `persist` is a declared output of every transition rather than a side
 *    effect at scattered call sites, so "what should be saved" is answered in
 *    the same place as "what should be shown".
 *  - Widths are derived from pointer positions here, so no caller needs to read
 *    state synchronously mid-gesture.
 */
export interface ColumnResizeState {
  columns: ColumnDefinition[];
  widths: ColumnWidths;
  drag: DragState | null;
  /**
   * Widths owed to the persisted panel config, or null when this transition
   * saves nothing. A fresh object every time a write is owed, so the React
   * layer can fire on identity alone.
   */
  persist: ColumnWidths | null;
  /**
   * Until the first non-empty column set arrives, every saved width would look
   * like it belonged to a departed column.
   */
  hasReceivedColumns: boolean;
}

interface DragState {
  columnId: string;
  startWidth: number;
  startClientX: number;
  /** Widths as they stood before this drag pinned every flexing column. */
  baseline: ColumnWidths;
  /** Whether a pointermove has actually changed the width. */
  moved: boolean;
  /**
   * Whether anything was persisted while this drag was in flight. A cancel has
   * to re-save the rollback when so, because the stored config no longer
   * matches what the drag started from.
   */
  persistedDuring: boolean;
}

export type ColumnResizeEvent =
  | { type: "columnsChanged"; columns: ColumnDefinition[] }
  | {
      type: "pointerDown";
      columnId: string;
      clientX: number;
      measure: (index: number) => number | undefined;
    }
  | { type: "pointerMove"; columnId: string; clientX: number }
  | { type: "pointerUp"; columnId: string }
  | { type: "pointerAbort"; columnId: string };

export function createColumnResizeState(
  widths: ColumnWidths,
): ColumnResizeState {
  return {
    columns: [],
    widths,
    drag: null,
    persist: null,
    hasReceivedColumns: false,
  };
}

// Clearing `persist` on transitions that save nothing is what stops the React
// layer re-firing a stale write; keep the identity when it is already null so
// unrelated events don't churn renders.
function saveNothing(state: ColumnResizeState): ColumnResizeState {
  return state.persist === null ? state : { ...state, persist: null };
}

function isPresent(columns: ColumnDefinition[], columnId: string): boolean {
  return (
    columnId === TIME_COLUMN_ID || columns.some((c) => c.columnId === columnId)
  );
}

// Abandon the drag and return to the widths it started from. Re-saving is only
// needed when the drag's own lifetime wrote to the config; otherwise the stored
// copy already matches the baseline, and writing would put a config update
// behind every stray click on a handle.
function rollBack(
  state: ColumnResizeState,
  drag: DragState,
): ColumnResizeState {
  return {
    ...state,
    widths: drag.baseline,
    drag: null,
    persist: drag.persistedDuring ? drag.baseline : null,
  };
}

export function columnResizeReducer(
  state: ColumnResizeState,
  event: ColumnResizeEvent,
): ColumnResizeState {
  switch (event.type) {
    case "columnsChanged": {
      const columns = event.columns;
      const hasReceivedColumns = state.hasReceivedColumns || columns.length > 0;
      if (!hasReceivedColumns) {
        return { ...saveNothing(state), columns, hasReceivedColumns };
      }

      // The dragged column itself can depart. Its drag has no target left, so
      // end it rather than let subsequent moves pin a key no column matches.
      const drag = state.drag;
      if (drag !== null && !isPresent(columns, drag.columnId)) {
        const widths = reclaimRemovedWidths(columns, drag.baseline);
        return {
          ...state,
          columns,
          hasReceivedColumns,
          widths,
          drag: null,
          persist: widths,
        };
      }

      const widths = reclaimRemovedWidths(columns, state.widths);
      if (widths === state.widths) {
        return { ...saveNothing(state), columns, hasReceivedColumns };
      }

      return {
        ...state,
        columns,
        hasReceivedColumns,
        widths,
        // Rebase the rollback through the same reclaim, or cancelling would
        // restore the departed column's width and contradict what is saved.
        drag:
          drag === null
            ? null
            : {
                ...drag,
                baseline: reclaimRemovedWidths(columns, drag.baseline),
                persistedDuring: true,
              },
        persist: widths,
      };
    }

    case "pointerDown": {
      // One resize at a time. A second concurrent drag would need its own
      // rollback, and two rollbacks over one width map have no sane resolution.
      //
      // A column the reducer doesn't know about is refused too: nothing should
      // be pinned under a key that no track will ever be drawn for.
      if (state.drag !== null || !isPresent(state.columns, event.columnId)) {
        return saveNothing(state);
      }
      const baseline = state.widths;
      // Freeze every still-flexing column, so this drag only moves its own.
      const widths = snapshotMissingWidths(
        state.columns,
        baseline,
        event.measure,
      );
      return {
        ...state,
        widths,
        drag: {
          columnId: event.columnId,
          startWidth: widths[event.columnId] ?? MIN_COLUMN_WIDTH_PX,
          startClientX: event.clientX,
          baseline,
          moved: false,
          persistedDuring: false,
        },
        persist: null,
      };
    }

    case "pointerMove": {
      const drag = state.drag;
      if (drag === null || drag.columnId !== event.columnId) {
        return saveNothing(state);
      }
      const width = computeResizedWidth(
        drag.startWidth,
        drag.startClientX,
        event.clientX,
      );
      if (width === state.widths[drag.columnId]) {
        return saveNothing(state);
      }
      return {
        ...state,
        widths: { ...state.widths, [drag.columnId]: width },
        drag: { ...drag, moved: width !== drag.startWidth },
        // A live preview is not saved; only the width the gesture settles on.
        persist: null,
      };
    }

    case "pointerUp": {
      const drag = state.drag;
      if (drag === null || drag.columnId !== event.columnId) {
        return saveNothing(state);
      }
      // A press that never moved the column is not a resize. Committing would
      // save a half-finished snapshot; simply dropping it would strand the
      // pin-everything freeze taken on pointerdown.
      if (!drag.moved) {
        return rollBack(state, drag);
      }
      return { ...state, drag: null, persist: state.widths };
    }

    case "pointerAbort": {
      const drag = state.drag;
      if (drag === null || drag.columnId !== event.columnId) {
        return saveNothing(state);
      }
      // The gesture was taken away rather than finished, so whatever width the
      // preview reached was never a choice the user made.
      return rollBack(state, drag);
    }
  }
}
